//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, {
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

import {
  BodyText,
  CaptionSecondaryText,
  MetatagText,
  UiCopySecondaryText,
} from '@components/atoms/text/Text';
import {
  ActionSheetButton,
  IOwnProps as ActionSheetProps,
  IActionSheetButton,
} from '@components/molecules/action-sheet/ActionSheet';
import Popover, {
  Props as PopoverProps,
} from '@components/molecules/popover/Popover';
import { usePopover } from '@components/molecules/popover/usePopover';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { isDefined } from '@utils/filterPredicates';
import { groupBy } from '@utils/groupBy';
import { chunkStringBySubstring } from '@utils/stringUtils';
import { pxStrToNum } from '@utils/styleUtils';

/*
  This is the max container height that allows the typeahead to show
  5 results each for both hashtags and usernames
*/
const MAX_CONTAINER_HEIGHT = 525;

/*
  This is the max container height that allows the typeahead to show
  10 full results and the 11th being half for hashtags or usernames only search
*/
const MAX_CONTAINER_HEIGHT_WITH_PREFIX = 480;

export interface ISearchTypeAheadData {
  count: number;
  shouldShowCount?: boolean;
  header: string;
  id?: string | number;
  label: string;
  labelSubtitle?: string;
  prefix?: string;
  prefixIcon?: ReactNode;
  type?: string;
}

type SearchTypeAheadActionSheetButton = ISearchTypeAheadData &
  IActionSheetButton;

type SearchTypeAheadPopoverProps = Omit<
  PopoverProps,
  'shouldShowArrow' | 'shouldOpenOnHover' | 'target' | 'content'
>;

interface SearchTypeAheadProps
  extends Omit<ActionSheetProps, 'children'>,
    SearchTypeAheadPopoverProps {
  actionSheetConfig: SearchTypeAheadActionSheetButton[];
  children: (open: () => void, close: () => void) => ReactElement;
  ellipsize?: number;
  shouldShowHeader?: boolean;
  isLoading: boolean;
  resultsLabel?: string;
  searchEmptyLabel?: string;
  searchingLabel?: string;
  searchPrefix: string;
  searchTerm: string;
}

export const SearchTypeAhead = ({
  actionSheetConfig,
  children,
  className,
  ellipsize,
  isLoading,
  onToggleActionSheet = () => undefined,
  resultsLabel = 'items',
  searchEmptyLabel = 'No results found',
  searchPrefix,
  searchTerm,
  searchingLabel = 'Searching...',
  shouldShowHeader = true,
  ...popoverProps
}: SearchTypeAheadProps) => {
  const [connectPopover, { closePopover, openPopover }] = usePopover();
  // This state reflects whether the action sheet is currently open. It does not control the rendering of the action sheet.
  const [isActionSheetOpen, setIsActionSheetOpen] = useState(false);

  const toggleSheet = (isOpen: boolean, event: Event) => {
    event.preventDefault();
    event.stopPropagation();

    onToggleActionSheet(isOpen);
    setIsActionSheetOpen(isOpen);
  };

  const handleActionButtonClick = (
    event: React.MouseEvent,
    actionButton: SearchTypeAheadActionSheetButton,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    actionButton.onClick?.(event);

    closePopover();
    onToggleActionSheet(false);
    setIsActionSheetOpen(false);
  };

  const renderActionButtons = (
    actionButtons: SearchTypeAheadActionSheetButton[] | undefined,
  ) => {
    if (!isDefined(actionButtons)) {
      return <></>;
    }

    const searchText = searchTerm.replace(searchPrefix, '');

    return actionButtons.map((actionButton, idx) => {
      return (
        <SearchTypeAheadActionButton
          key={`${actionButton.header}-${actionButton.label}-${idx}`}
          actionButton={actionButton}
          ellipsize={ellipsize}
          handleActionButtonClick={handleActionButtonClick}
          isActionSheetOpen={isActionSheetOpen}
          resultsLabel={resultsLabel}
          searchText={searchText}
        />
      );
    });
  };

  const renderItems = (actionButtons: SearchTypeAheadActionSheetButton[]) => {
    const groupedActionButtons = groupBy(
      actionButtons,
      (actionButton) => actionButton.header,
    );

    return Object.keys(groupedActionButtons).map((header, headerIdx) => (
      <div key={header}>
        {headerIdx !== 0 && <ActionDivider />}
        {shouldShowHeader && (
          <ActionSheetHeader>
            {searchTerm ? `Matching ${header}` : `Top ${header}`}
          </ActionSheetHeader>
        )}
        {renderActionButtons(groupedActionButtons[header])}
      </div>
    ));
  };

  const shouldShowEmptyState = !actionSheetConfig.length && !isLoading;

  const contents = (
    <ContentsWrapper className={className} hasPrefix={!!searchPrefix}>
      <div>
        {shouldShowEmptyState ? (
          <ContextText>{searchEmptyLabel}</ContextText>
        ) : isLoading ? (
          <ContextText>{searchingLabel}</ContextText>
        ) : (
          renderItems(actionSheetConfig)
        )}
      </div>
    </ContentsWrapper>
  );

  return (
    <Popover
      connectPopover={connectPopover}
      content={contents}
      onTogglePopover={toggleSheet}
      shouldOpenOnHover={false}
      shouldShowArrow={false}
      target={children(openPopover, closePopover)}
      {...popoverProps}
    />
  );
};

const isOverflow = (element: HTMLElement | null): boolean => {
  if (!element?.firstElementChild) {
    return false;
  }

  const computedStyle = window.getComputedStyle(element);
  const leftPadding = computedStyle.paddingLeft;
  const rightPadding = computedStyle.paddingRight;
  const paddingX = pxStrToNum(leftPadding) + pxStrToNum(rightPadding);

  return (
    element.clientWidth - paddingX <= element.firstElementChild.clientWidth // Minus paddings from button element
  );
};

interface SearchTypeAheadActionButtonProps {
  actionButton: SearchTypeAheadActionSheetButton;
  ellipsize?: number;
  handleActionButtonClick: (
    event: React.MouseEvent,
    actionButton: SearchTypeAheadActionSheetButton,
  ) => void;
  isActionSheetOpen: boolean;
  resultsLabel?: string;
  searchText: string;
}

const SearchTypeAheadActionButton = ({
  actionButton,
  ellipsize,
  handleActionButtonClick,
  isActionSheetOpen,
  resultsLabel,
  searchText,
}: SearchTypeAheadActionButtonProps) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [shouldShowTooltip, setShouldShowTooltip] = useState<boolean>(false);

  // `isActionSheetOpen` required as dependency since component might be rendered when action sheet is hidden and we cannot get dimensions of hidden elements.
  useEffect(() => {
    if (!isActionSheetOpen || !ellipsize) {
      return;
    }

    setShouldShowTooltip(isOverflow(buttonRef.current));
  }, [buttonRef, isActionSheetOpen, ellipsize]);

  const labelSplitOnSearchTerm = chunkStringBySubstring(
    searchText,
    actionButton.label,
  );

  const prefix = actionButton.prefix ?? '';

  return (
    <SmallTooltip
      appendTarget='body'
      content={`${prefix}${actionButton.label}`}
      interactive={false}
      isDisabled={!shouldShowTooltip}
      placement='left'
      target={
        <ActionSheetButton
          ref={buttonRef}
          onClick={(event) => handleActionButtonClick(event, actionButton)}
          rightContent={
            <>
              {/* If shouldShowCount is undefined, default to display count */}
              {(!isDefined(actionButton.shouldShowCount) ||
                actionButton.shouldShowCount) && (
                <SecondaryText>{`${actionButton.count} ${resultsLabel}`}</SecondaryText>
              )}
            </>
          }
          trackingId={actionButton.trackingId}
        >
          <ActionButtonContentWrapper>
            <ItemDetails ellipsize={ellipsize}>
              {actionButton.prefixIcon && (
                <PrefixIconWrappr>{actionButton.prefixIcon}</PrefixIconWrappr>
              )}
              {prefix}
              <ItemInfoContainer>
                <span>
                  {labelSplitOnSearchTerm.map((substring, substringIdx) => {
                    return substring.toLowerCase() ===
                      searchText.toLowerCase() ? (
                      <Highlighter key={`${substring}-${substringIdx}`}>
                        {substring}
                      </Highlighter>
                    ) : (
                      substring
                    );
                  })}
                </span>
                {isDefined(actionButton.labelSubtitle) && (
                  <CaptionSecondaryText>
                    {actionButton.labelSubtitle}
                  </CaptionSecondaryText>
                )}
              </ItemInfoContainer>
            </ItemDetails>
          </ActionButtonContentWrapper>
        </ActionSheetButton>
      }
    />
  );
};

const ActionDivider = styled.hr(
  ({ theme }) => `
  /* Spacing on both side should be 12px */
  margin: ${theme.spacings[2]} calc(${theme.spacings[2]} + ${theme.spacings[1]});
  `,
);

const Highlighter = styled.span(
  ({ theme }) => `
  font-weight: ${theme.fontWeights.bold};
  `,
);

const ActionButtonContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ItemDetails = styled(BodyText)(
  ({ theme }) => `
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    font-size: ${theme.fontSizes[5]};
    text-align: start;
    color: ${theme.colors.gray9};
`,
);

const ItemInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const SecondaryText = styled(UiCopySecondaryText)(
  ({ theme }) => `
  /* Spacing on both side should be 12px */
  padding: calc(${theme.spacings[2]} + ${theme.spacings[1]});
`,
);

const ContentsWrapper = styled.div<{
  hasPrefix: boolean;
}>(
  ({ hasPrefix, theme }) => `
  width: 100%;
  max-height: ${
    hasPrefix ? MAX_CONTAINER_HEIGHT_WITH_PREFIX : MAX_CONTAINER_HEIGHT
  }px;
  padding: ${theme.spacings[2]} 0;
  overflow-y: auto;
  `,
);

const ActionSheetHeader = styled(MetatagText)(
  ({ theme }) => `
  /* Spacing on both side should be 12px */
  padding: calc(${theme.spacings[2]} + ${theme.spacings[1]});
  `,
);

const ContextText = styled.div(
  ({ theme }) => `
  /* Spacing on both side should be 12px */
  padding: calc(${theme.spacings[2]} + ${theme.spacings[1]});
  font-style: italic;
  `,
);

const PrefixIconWrappr = styled.span(
  ({ theme }) => css`
    margin-right: ${theme.spacings[2]};
    vertical-align: middle;
  `,
);
